import {
  Box,
  Checkbox,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { ChangeEvent, Fragment, MouseEventHandler } from "react";
import { DataTable } from "../data-table";
import { Pagination, PaginationProps } from "../pagination";
import { Paginate, PaginateProps } from "../pagination/paginate";
import { FirewatchRow } from "../FirewatchRow/FirewatchRow";
import {
  AdjustedAggregateScoreType,
  AggregateScoresEdges,
  AggregateScoresNode,
  SelectedEntityWithAggregateScoresData,
} from "../FirewatchPage";
import {
  getFirewatchColumns,
  Header,
} from "../FirewatchPage/FirewatchPageColumns";
import { Space } from "~/lib/types";
import { Loading } from "../loading";
import { pageNameByType } from "../FirewatchPage/lib";
import { ScopeType, SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { SwitchBaseProps } from "@mui/material/internal/SwitchBase";
import { SelectedEntityWithFindingData } from "~/components/exceptions/SetExceptionDialog";
import { AggregateScoreType, ScoreRating, OrderDirection } from "~/operations";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { SelectedEntityWithTicketData } from "~/pages/tickets/types";
import { FindingsTablePreHeader } from "~/components/FirewatchList/components/FindingsTablePreHeader";
import { Link as RouterLink } from "react-router-dom";

interface FirewatchListProps {
  size?: "small" | "list";
  scope?: SpaceOrWorkspaceScope;
  space: Space;
  loading: boolean;
  data: AggregateScoresEdges;
  pageType: AdjustedAggregateScoreType;
  sort?: ReturnType<typeof useSort>;
  paginationProps?: PaginationProps;
  paginateProps?: PaginateProps;
  disableSorting?: boolean;
  noTags?: boolean;
  queryParams?: Record<string, string>;
  selectionProps?: {
    canSelect: boolean;
    isGroupChecked: boolean;
    isGroupIndeterminate: boolean;
    onAllCheckboxClick?: SwitchBaseProps["onChange"];
    selectedEntities: Array<
      SelectedEntityWithFindingData &
        SelectedEntityWithException &
        SelectedEntityWithTicketData &
        SelectedEntityWithAggregateScoresData
    >;
    onRowCheckboxChange: (
      e: ChangeEvent<HTMLInputElement>,
      checked: boolean,
      entity: SelectedEntityWithFindingData &
        SelectedEntityWithException &
        SelectedEntityWithTicketData &
        SelectedEntityWithAggregateScoresData,
    ) => void;
    onRowCheckboxClick: MouseEventHandler;
  };
  isCVERemediation?: boolean;
  disableTakeAction?: boolean;
}

export function FirewatchList({
  size,
  space,
  scope,
  pageType,
  data,
  loading,
  sort,
  paginationProps,
  paginateProps,
  disableSorting = false,
  noTags,
  queryParams,
  selectionProps,
  isCVERemediation,
  disableTakeAction,
}: FirewatchListProps) {
  const isSmallTable = size === "small";

  const tableHeaders = getFirewatchColumns({
    pageType,
    isSmallTable,
    isSearchResults: false,
    isOrg: false,
    isCVERemediation,
    selectable: selectionProps?.canSelect,
    isSpaceDashboard: scope?.type === ScopeType.Space,
    disableTakeAction,
  });

  const sortableHeaders: Header[] = tableHeaders.filter(
    (header) => header.sortable,
  );

  return (
    <Fragment>
      {!isSmallTable && (
        <FindingsTablePreHeader
          sort={sort}
          isSortDisable={disableSorting}
          sortableHeaders={sortableHeaders}
        />
      )}

      <DataTable
        firewatchSize={size}
        selectable={selectionProps?.canSelect}
        selection={selectionProps?.selectedEntities}
      >
        <TableHead
          sx={{
            ...(isSmallTable && {
              backgroundColor: "inherit",
              "& th": {
                fontSize: 10,
                color: "text.secondary",
                textTransform: "uppercase",
                borderBottom: "none",
                pl: 0,
              },
              "&:hover": {
                backgroundColor: "transparent",
              },
            }),
          }}
        >
          <TableRow>
            {tableHeaders.map((header, index) => {
              const { sortable } = header;
              const isSortable = sortable && !disableSorting && sort;

              if (header.id === "SELECT" && selectionProps?.canSelect) {
                return (
                  <TableCell key={`${header.id}-${index}`} width={50}>
                    <Checkbox
                      checked={selectionProps?.isGroupChecked}
                      indeterminate={selectionProps?.isGroupIndeterminate}
                      onChange={selectionProps?.onAllCheckboxClick}
                    />
                  </TableCell>
                );
              }

              return (
                <TableCell
                  key={header.field}
                  align={header.options?.textAlign}
                  width={header.options?.width}
                  sx={{
                    ...header.options?.sx,
                    ...(isSmallTable && {
                      fontWeight: "500 !important",
                    }),
                  }}
                >
                  {isSortable && (
                    <TableSortLabel
                      component={sort.baseUrl ? RouterLink : "span"}
                      to={
                        sort.baseUrl
                          ? sort.sortColumnUrl(header.field).href
                          : undefined
                      }
                      sx={{
                        ...(isSmallTable && {
                          "& .MuiTableSortLabel-icon": { fontSize: 10 },
                        }),
                      }}
                      onClick={
                        sort.baseUrl
                          ? () => sort.handleSortClick(header.field)
                          : undefined
                      }
                      direction={
                        sort.orderBy.direction === OrderDirection.Asc
                          ? "asc"
                          : "desc"
                      }
                      active={header.field === sort.orderBy.field}
                    >
                      {header.label}
                    </TableSortLabel>
                  )}

                  {!isSortable && header.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* If the data is loading, show a loading message */}
          {loading && (
            <TableRow className="loading-row">
              <TableCell
                sx={{ border: 0 }}
                colSpan={
                  tableHeaders.length + (selectionProps?.canSelect ? 1 : 0)
                }
              >
                <Box sx={{ my: 10 }}>
                  <Loading what={`${pageNameByType(pageType)}`} />
                </Box>
              </TableCell>
            </TableRow>
          )}

          {!loading &&
            data?.map((edge) => (
              <FirewatchRow
                size={size}
                key={edge.cursor}
                space={space}
                scope={scope}
                pageType={pageType}
                tableHeaders={tableHeaders}
                row={edge.node as Omit<AggregateScoresNode, "nodeTitle">}
                noTags={noTags}
                queryParams={queryParams}
                selectionProps={{
                  canSelect: Boolean(selectionProps?.canSelect),
                  isSelected: Boolean(
                    selectionProps?.selectedEntities.find(
                      (selectedCheck) =>
                        edge.node?.findingMrn === selectedCheck.mrn,
                    ),
                  ),
                  onCheckboxClick: (e) => {
                    selectionProps?.onRowCheckboxClick(e);
                  },
                  onCheckboxSelect: (event, checked) => {
                    if (!scope) return;

                    selectionProps?.onRowCheckboxChange(event, checked, {
                      mrn: edge?.node?.findingMrn || "",
                      rating: edge?.node?.rating || ScoreRating.Unscored,
                      title: edge?.node?.title || "",
                      exception: edge?.node?.exception,
                      scopeMrn: scope.mrn,
                      isAffected: (edge?.node?.blastRadius?.affected || 0) > 0,
                      scoreType:
                        edge?.node?.scoreType || AggregateScoreType.Check,
                    });
                  },
                }}
              />
            ))}
        </TableBody>
      </DataTable>
      {paginationProps && !loading && <Pagination {...paginationProps} />}
      {paginateProps && !loading && <Paginate {...paginateProps} />}
    </Fragment>
  );
}
